<template>
  <v-select ref="custom-vue-select" :append-to-body="appendToBody" :calculate-position="calculatePosition ?? defaultCalculatePosition">
    <template #selected-option="option">
      <div class="d-flex align-items-center">
        <component :is="option.icon" class="me-2" :fill="iconColor" />
        <div class="label-custom-vue-select">{{ option[label] }}</div>
      </div>
    </template>
    <template v-slot:option="option">
      <component :is="option.icon" class="me-2" fill="#606A6C" />
      {{ option[label] }}
    </template>
    <template #open-indicator="{ attributes }">
      <span v-bind="attributes" style="display: flex; align-items: center"><down-ward-icon :fill="downWardIconColor" /></span>
    </template>
  </v-select>
</template>

<script setup lang="ts">
import DownWardIcon from '@/assets/icons/base/v-select/DownWardIcon.vue';
import { createVSelectDropdownPositioner } from '@/utils/vSelectAppendToBody';

// Default to append-to-body so the dropdown <ul> teleports to <body> and can never be clipped
// by an ancestor `overflow` (modals, scrollable panels, cards) — the recurring "options behind
// / clipped" bug. The positioner re-applies the app font + copies the inherited `--vs-*`
// theming onto the teleported <ul> so per-screen colors/sizing survive. A consumer that must
// keep the dropdown inline (e.g. a menu explicitly positioned relative to its wrapper) can
// pass :append-to-body="false", and a custom :calculate-position still wins over the default.
type VSelectCalculatePosition = (
  dropdownList: HTMLElement,
  component: unknown,
  position: { width: string; top: string; left: string },
) => void;

interface Props {
  iconColor?: string;
  downWardIconColor?: string;
  label?: string;
  appendToBody?: boolean;
  calculatePosition?: VSelectCalculatePosition;
}

withDefaults(defineProps<Props>(), {
  iconColor: '#007fff',
  downWardIconColor: '#007fff',
  label: 'label',
  appendToBody: true,
  calculatePosition: undefined,
});

const defaultCalculatePosition = createVSelectDropdownPositioner();
</script>
